import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI, projectsAPI, contentAPI } from '../utils/api';
import RichTextEditor from '../components/RichTextEditor';

const Admin = () => {
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('projects');
  const [homeContent, setHomeContent] = useState('');
  const [cvContent, setCvContent] = useState('');
  const [slideshowImages, setSlideshowImages] = useState([]);
  const [slideshowIsDefault, setSlideshowIsDefault] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    checkAuth();
    loadProjects();
    loadContent();
    loadSlideshowImages();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
    } catch (error) {
      navigate('/admin/login');
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadContent = async () => {
    try {
      const [homeRes, cvRes] = await Promise.all([
        contentAPI.getPageContent('home'),
        contentAPI.getPageContent('cv'),
      ]);
      setHomeContent(homeRes.data.content || '');
      setCvContent(cvRes.data.content || '');
    } catch (error) {
      console.error('Failed to load content:', error);
    }
  };

  const loadSlideshowImages = async () => {
    try {
      const response = await contentAPI.getSlideshowImages();
      // Handle both API response formats
      const images = response.data.slideshow_images || response.data.images || [];
      const isDefault = response.data.is_default || false;
      setSlideshowImages(images);
      setSlideshowIsDefault(isDefault);
    } catch (error) {
      console.error('Failed to load slideshow images:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await projectsAPI.delete(id);
      loadProjects();
    } catch (error) {
      alert('Failed to delete project');
    }
  };

  const handleSaveHomeContent = async () => {
    try {
      await contentAPI.updatePageContent('home', homeContent);
      alert('Home content saved successfully!');
    } catch (error) {
      alert('Failed to save home content');
    }
  };

  const handleSaveCVContent = async () => {
    try {
      await contentAPI.updatePageContent('cv', cvContent);
      alert('CV content saved successfully!');
    } catch (error) {
      alert('Failed to save CV content');
    }
  };

  const handleUploadProfileImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      await contentAPI.uploadProfileImage(file);
      alert('Profile image uploaded successfully!');
    } catch (error) {
      alert('Failed to upload profile image');
    }
  };

  const handleUploadDemoreel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      await contentAPI.uploadDemoreel(file);
      alert('Demoreel uploaded successfully!');
    } catch (error) {
      alert('Failed to upload demoreel');
    }
  };

  const handleUploadCVImages = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      await contentAPI.uploadCVImages(files);
      alert('CV images uploaded successfully!');
    } catch (error) {
      alert('Failed to upload CV images');
    }
  };

  const handleUploadSlideshowImages = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      await contentAPI.uploadSlideshowImages(files);
      alert('Slideshow images uploaded successfully!');
      loadSlideshowImages();
    } catch (error) {
      alert('Failed to upload slideshow images');
    }
  };

  const handleDeleteSlideshowImage = async (index) => {
    if (!window.confirm('Are you sure you want to delete this slideshow image?')) return;

    try {
      await contentAPI.deleteSlideshowImage(index);
      alert('Slideshow image deleted successfully!');
      loadSlideshowImages();
    } catch (error) {
      alert('Failed to delete slideshow image');
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-page">
      {/* Header */}
      <div className="bg-black text-white p-4">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            <span className="text-gray-400">Welcome, {user.username}</span>
          </div>
          <div className="flex gap-4">
            <Link to="/" className="text-white hover:text-gray-300" data-testid="admin-view-site">View Site</Link>
            <button onClick={handleLogout} className="text-white hover:text-gray-300" data-testid="admin-logout">Logout</button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="container mx-auto">
          <div className="flex gap-2">
            {['projects', 'home', 'cv', 'slideshow'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 font-semibold transition-colors ${
                  activeTab === tab
                    ? 'bg-black text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`admin-tab-${tab}`}
              >
                {tab.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto p-6">
        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div data-testid="admin-projects-tab">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Manage Projects</h2>
              <Link
                to="/admin/project/new"
                className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800"
                data-testid="admin-add-project-button"
              >
                Add New Project
              </Link>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Order</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Image</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Year</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Details</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {projects.map((project) => (
                      <tr key={project.id} className="hover:bg-gray-50" data-testid={`admin-project-row-${project.id}`}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">{project.order}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <img
                            src={`${BACKEND_URL}${project.main_image}`}
                            alt={project.title}
                            className="h-16 w-12 object-cover rounded"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{project.title}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{project.role}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{project.company}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{project.year}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">{project.category}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <div className="flex gap-1 justify-center">
                            {project.description && (
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 text-green-800 rounded text-xs font-semibold" title="Has description">D</span>
                            )}
                            {project.extra_images && project.extra_images.length > 0 && (
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 rounded text-xs font-semibold" title={`${project.extra_images.length} extra images`}>I</span>
                            )}
                            {project.video_url && (
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-purple-100 text-purple-800 rounded text-xs font-semibold" title="Has video">V</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="flex gap-2 justify-end">
                            <Link
                              to={`/admin/project/edit/${project.id}`}
                              className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                              data-testid={`admin-edit-project-${project.id}`}
                            >
                              Edit
                            </Link>
                            <button
                              onClick={() => handleDeleteProject(project.id)}
                              className="text-red-600 hover:text-red-900 text-sm font-medium"
                              data-testid={`admin-delete-project-${project.id}`}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Home Tab */}
        {activeTab === 'home' && (
          <div data-testid="admin-home-tab">
            <h2 className="text-2xl font-bold mb-6">Edit Home Page</h2>
            
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Profile Image</h3>
              <input
                type="file"
                accept="image/*"
                onChange={handleUploadProfileImage}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
                data-testid="admin-upload-profile-image"
              />
            </div>

            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Presentation Text</h3>
              <RichTextEditor
                value={homeContent}
                onChange={setHomeContent}
                placeholder="Enter your presentation text..."
                height="250px"
              />
              <button
                onClick={handleSaveHomeContent}
                className="mt-4 bg-black text-white px-6 py-2 rounded hover:bg-gray-800"
                data-testid="admin-save-home-content"
              >
                Save Home Content
              </button>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Demoreel Video</h3>
              <input
                type="file"
                accept="video/*"
                onChange={handleUploadDemoreel}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
                data-testid="admin-upload-demoreel"
              />
            </div>
          </div>
        )}

        {/* CV Tab */}
        {activeTab === 'cv' && (
          <div data-testid="admin-cv-tab">
            <h2 className="text-2xl font-bold mb-6">Edit CV</h2>
            
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">CV Images</h3>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleUploadCVImages}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
                data-testid="admin-upload-cv-images"
              />
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">CV Content</h3>
              <RichTextEditor
                value={cvContent}
                onChange={setCvContent}
                placeholder="Enter your CV content..."
                height="400px"
              />
              <button
                onClick={handleSaveCVContent}
                className="mt-4 bg-black text-white px-6 py-2 rounded hover:bg-gray-800"
                data-testid="admin-save-cv-content"
              >
                Save CV Content
              </button>
            </div>
          </div>
        )}

        {/* Slideshow Tab */}
        {activeTab === 'slideshow' && (
          <div data-testid="admin-slideshow-tab">
            <h2 className="text-2xl font-bold mb-6">Manage Slideshow Images</h2>
            
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Add New Images</h3>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleUploadSlideshowImages}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
                data-testid="admin-upload-slideshow-images"
              />
              <p className="text-sm text-gray-500 mt-2">Upload high-resolution landscape images for best results</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Current Slideshow Images</h3>
                {slideshowIsDefault && (
                  <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded">Using default images from original site</span>
                )}
              </div>
              {slideshowImages.length === 0 ? (
                <p className="text-gray-500">No slideshow images yet. Upload some to get started!</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {slideshowImages.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={img.startsWith('http') ? img : `${BACKEND_URL}${img}`}
                        alt={`Slideshow ${index + 1}`}
                        className="w-full h-48 object-cover rounded"
                      />
                      {!slideshowIsDefault && (
                        <button
                          onClick={() => handleDeleteSlideshowImage(index)}
                          className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1 rounded text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-slideshow-${index}`}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
